<template>
  <div
    class="media-item"
    :class="{
      'selected': selected,
      'hovering': hovering,
      'selectable': selectable
    }"
    @click="handleClick"
    @mouseenter="$emit('mouseenter', media.id)"
    @mouseleave="$emit('mouseleave')"
  >
    <!-- Selection checkbox -->
    <div v-if="selectable" class="media-item-checkbox">
      <CIcon
        name="cipCheckbox"
        size="custom-size"
        :width="20"
        :class="['checkbox-icon', {'selected': selected}]"
        @click.stop="handleClick"
      />
    </div>

    <!-- Badge overlay (top right) -->
    <div v-if="badge" class="media-item-badge">
      <CBadge :color="badge.color || 'light'" shape="pill">
        <CIcon v-if="badge.icon" :name="badge.icon" size="sm" class="mr-1"/>
        {{ badge.text }}
      </CBadge>
    </div>

    <!-- Thumbnail -->
    <img
      :src="media.thumb"
      :alt="`Photo ${media.id}`"
      @error="handleImageError"
      class="media-item-image"
    />

    <!-- Zoom button -->
    <CLink
      v-if="zoomable && (media.sd || media.hd)"
      class="media-item-zoom-btn"
      @click.stop="handleZoom"
      :title="$gettext('View full size')"
    >
      <CIcon name="cipResize" size="custom-size" :width="18" class="text-primary"/>
    </CLink>

    <!-- Selection indicator overlay -->
    <div v-if="selected" class="media-item-selected-overlay"></div>
  </div>
</template>

<script>
export default {
  name: 'MediaItem',
  props: {
    media: {
      type: Object,
      required: true
      // Expected: { id, thumb, sd?, hd? }
    },
    selected: {
      type: Boolean,
      default: false
    },
    hovering: {
      type: Boolean,
      default: false
    },
    badge: {
      type: Object,
      default: null
      // Format: { text, color, icon }
    },
    selectable: {
      type: Boolean,
      default: true
    },
    zoomable: {
      type: Boolean,
      default: true
    }
  },

  methods: {
    handleClick(event) {
      if (!this.selectable) return
      this.$emit('click', event, this.media.id)
    },

    handleZoom() {
      if (!this.zoomable) return
      this.$emit('zoom', this.media)
    },

    handleImageError(event) {
      // Try SD version if thumb fails
      if (this.media.sd && event.target.src !== this.media.sd) {
        event.target.src = this.media.sd
        return
      }
      // Final fallback
      event.target.src = '/img/placeholder-image.png'
    }
  }
}
</script>

<style scoped>
.media-item {
  position: relative;
  aspect-ratio: 1;
  border: 2px solid #dee2e6;
  border-radius: 4px;
  overflow: hidden;
  transition: all 0.2s;
  background: #f8f9fa;
  cursor: default;
}

.media-item.selectable {
  cursor: pointer;
}

.media-item:hover {
  border-color: #007bff;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.media-item:hover .media-item-checkbox,
.media-item.selected .media-item-checkbox {
  display: block;
}

/* Always show checkbox on mobile */
@media (max-width: 992px) {
  .media-item-checkbox {
    display: block !important;
  }
}

.media-item.selected {
  border-color: #007bff;
  background: #e7f3ff;
  transform: scale(0.95);
}

/* Hovering state for shift+click range selection */
.media-item.hovering {
  border-color: #007bff;
  background: #cce5ff;
  opacity: 0.7;
  transform: scale(0.97);
}

.media-item.hovering.selected {
  background: #e7f3ff;
  opacity: 1;
  transform: scale(0.95);
}

.media-item-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.media-item-checkbox {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 10;
  display: none;
  cursor: pointer;
}

.checkbox-icon {
  display: block;
  cursor: pointer;
  transition: all 0.2s;
}

.checkbox-icon :deep(.box-check) {
  stroke: #333;
  fill: rgba(255, 255, 255, 0.9);
}

.checkbox-icon :deep(.line-check) {
  display: none;
}

.checkbox-icon.selected :deep(.box-check) {
  fill: #007bff;
  stroke: #007bff;
}

.checkbox-icon.selected :deep(.line-check) {
  display: block;
}

.media-item-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
}

.media-item-zoom-btn {
  position: absolute;
  bottom: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  z-index: 2;
}

.media-item-zoom-btn:hover {
  background: white;
  transform: scale(1.05);
  box-shadow: 0 2px 8px rgba(0,0,0,0.3);
}

.media-item-selected-overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
  border: 3px solid #007bff;
  border-radius: 2px;
}
</style>
